import { useEffect, useRef } from 'react';

interface UseScrollToFeatureOptions {
  targetFeatureId: string | null | undefined;
  isEnabled?: boolean;
  delay?: number;
}

/**
 * Custom hook to scroll to a specific feature row
 * @param targetFeatureId - The ID of the feature to scroll to
 * @param isEnabled - Whether scrolling is enabled (e.g., accordion is expanded)
 * @param delay - Delay in milliseconds before scrolling (default: 600ms)
 */
export const useScrollToFeature = ({
  targetFeatureId,
  isEnabled = true,
  delay = 600
}: UseScrollToFeatureOptions) => {
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (targetFeatureId && isEnabled) {
      // Clear any existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Scroll to specific feature row after a delay to ensure rendering is complete
      scrollTimeoutRef.current = setTimeout(() => {
        const featureElement = document.querySelector(
          `[data-testid="features-row-${targetFeatureId}"]`
        );
        if (featureElement) {
          featureElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }
      }, delay);
    }

    // Cleanup timeout on unmount
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [targetFeatureId, isEnabled, delay]);
};
